import { useState } from "react";
import { useParams } from "react-router-dom";
import {
  Typography,
  Button,
  Stack,
  Switch,
  TextField,
  Paper,
  Card,
  CardContent,
  Box,
  IconButton,
  useTheme,
  useMediaQuery,
  MenuItem,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { GridColDef } from "@mui/x-data-grid";
import { PremiumDataGrid } from "../components/PremiumDataGrid";

import { useCollection } from "../hooks/useCollection";
import { Product } from "../interfaces/product";
import { ProductSelector } from "../components/ProductSelector";
import { SliceModeSelector } from "../components/SliceModeSelector";
import { PizzaFlavorSelector } from "../components/PizzaFlavorSelector";
import { Promotion } from "../interfaces/Promotion";
import { PizzaFlavor } from "./FlavorsPage";
import { useAuth } from "../hooks/useAuth";

const emptyPromotion: Promotion = {
  title: "",
  description: "",
  startDate: "",
  endDate: "",
  discountType: "percentage",
  discountValue: 10,
  active: true,
  productIds: [],
  sliceModes: [],
  flavorIds: [],
  recurrence: { type: "none" },
};

export default function PromotionsPage() {
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const {
    data: promotions,
    createItem,
    updateItem,
    deleteItem,
  } = useCollection<Promotion>(restaurantId ?? "", "promotions");

  const { data: products } = useCollection<Product>(
    restaurantId ?? "",
    "products"
  );

  const { data: flavors } = useCollection<PizzaFlavor>(
    restaurantId ?? "",
    "flavors"
  );

  const { canCreate, canEdit, canDelete } = useAuth();

  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState<Promotion>(emptyPromotion);

  const formatDateTime = (dateStr: string) => {
    if (!dateStr) return "N/A";
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? "N/A" : d.toLocaleString("es-CO", {
      dateStyle: "short",
      timeStyle: "short"
    });
  };

  /* ------------------ COLUMNS ------------------ */
  const columns: GridColDef[] = [
    { field: "title", headerName: "Título", flex: 1 },
    { 
      field: "startDate", 
      headerName: "Inicio", 
      width: 140,
      renderCell: (p) => formatDateTime(p.value)
    },
    { 
      field: "endDate", 
      headerName: "Fin", 
      width: 140,
      renderCell: (p) => formatDateTime(p.value)
    },
    { field: "discountType", headerName: "Tipo", width: 100 },
    { field: "discountValue", headerName: "Valor", width: 100 },
    {
      field: "active",
      headerName: "Activo",
      width: 100,
      renderCell: (p) => (p.value ? "Sí" : "No"),
    },
    {
      field: "actions",
      headerName: "Acciones",
      width: 160,
      renderCell: (p) => (
        <Stack direction="row" spacing={1}>
          {canEdit && (
            <Button
              size="small"
              onClick={() => {
                setCurrent(p.row as Promotion);
                setOpen(true);
              }}
            >
              Editar
            </Button>
          )}
          {canDelete && (
            <Button
              size="small"
              color="error"
              onClick={() => {
                  if (window.confirm("¿Seguro que deseas eliminar esta promoción?")) {
                      deleteItem(p.row.id);
                  }
              }}
            >
              Eliminar
            </Button>
          )}
        </Stack>
      ),
    },
  ];

  /* ------------------ HEADER ------------------ */
  const Header = (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={2}
      justifyContent="space-between"
      mb={2}
    >
      <Box>
        <Typography variant="h5" fontWeight={700}>Promociones Cronometradas</Typography>
        <Typography variant="body2" color="text.secondary">
          Crea promociones por tiempo limitado para tamaños y divisiones de porción específicos.
        </Typography>
      </Box>

      {canCreate && (
        <Button
          variant="contained"
          fullWidth={isMobile}
          onClick={() => {
            setCurrent(emptyPromotion);
            setOpen(true);
          }}
          sx={{ height: 'fit-content', py: 1.5 }}
        >
          Crear promoción
        </Button>
      )}
    </Stack>
  );

  /* ------------------ MOBILE ------------------ */
  const MobileList = (
    <Stack spacing={2}>
      {promotions.map((p) => (
        <Card key={p.id} sx={{ borderRadius: 3 }}>
          <CardContent>
            <Stack direction="row" spacing={2}>
              <Box flex={1}>
                <Typography fontWeight={600}>{p.title}</Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Inicio: {formatDateTime(p.startDate)}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Fin: {formatDateTime(p.endDate)}
                </Typography>
                <Typography variant="body2" mt={1}>
                  Descuento: {p.discountType === "percentage" ? `${p.discountValue}%` : `$${p.discountValue}`}
                </Typography>
                <Typography
                  variant="caption"
                  color={p.active ? "success.main" : "error.main"}
                  fontWeight={700}
                >
                  {p.active ? "ACTIVA" : "INACTIVA"}
                </Typography>
              </Box>

              <Stack>
                {canEdit && (
                  <IconButton
                    onClick={() => {
                      setCurrent(p);
                      setOpen(true);
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                )}

                {canDelete && (
                  <IconButton
                    color="error"
                    onClick={() => {
                        if (window.confirm("¿Seguro que deseas eliminar esta promoción?")) {
                            deleteItem(p.id!);
                        }
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      ))}
      {promotions.length === 0 && (
          <Typography color="text.secondary" textAlign="center" py={4}>No hay promociones creadas.</Typography>
      )}
    </Stack>
  );

  /* ------------------ FORM ------------------ */
  const Form = open && (
    <Paper sx={{ p: 3, mt: 3, borderRadius: 3 }}>
      <Stack spacing={3}>
        <Typography variant="h6" fontWeight={700}>
          {current.id ? "Editar promoción" : "Nueva promoción"}
        </Typography>

        <Stack spacing={2}>
            <TextField
            label="Título"
            value={current.title}
            onChange={(e) =>
                setCurrent({ ...current, title: e.target.value })
            }
            fullWidth
            required
            />
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
                label="Fecha de Inicio"
                type="datetime-local"
                value={current.startDate}
                onChange={(e) => setCurrent({ ...current, startDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
                required
            />
            <TextField
                label="Fecha de Fin"
                type="datetime-local"
                value={current.endDate}
                onChange={(e) => setCurrent({ ...current, endDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
                required
            />
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
                select
                label="Repetición"
                value={current.recurrence?.type || "none"}
                onChange={(e) => {
                  const type = e.target.value as any;
                  setCurrent({ 
                    ...current, 
                    recurrence: { 
                      ...current.recurrence, 
                      type, 
                      daysOfWeek: type === 'weekly' ? (current.recurrence?.daysOfWeek || []) : [] 
                    } 
                  });
                }}
                fullWidth
            >
                <MenuItem value="none">No se repite</MenuItem>
                <MenuItem value="daily">Todos los días (en el rango de horas)</MenuItem>
                <MenuItem value="weekly">Semanalmente (días específicos)</MenuItem>
                <MenuItem value="monthly">Mensualmente (mismo día del mes)</MenuItem>
            </TextField>
        </Stack>

        {current.recurrence?.type === 'weekly' && (
            <Stack direction="column" spacing={1}>
                <Typography variant="subtitle2" fontWeight={700}>Días de la semana</Typography>
                <Stack direction="row" flexWrap="wrap" gap={1}>
                    {[
                        { label: 'Dom', val: 0 },
                        { label: 'Lun', val: 1 },
                        { label: 'Mar', val: 2 },
                        { label: 'Mié', val: 3 },
                        { label: 'Jue', val: 4 },
                        { label: 'Vie', val: 5 },
                        { label: 'Sáb', val: 6 },
                    ].map((day) => {
                        const selected = current.recurrence?.daysOfWeek?.includes(day.val);
                        return (
                            <Button 
                                key={day.val}
                                variant={selected ? "contained" : "outlined"}
                                size="small"
                                sx={{ minWidth: '40px' }}
                                onClick={() => {
                                    const currDays = current.recurrence?.daysOfWeek || [];
                                    const newDays = selected ? currDays.filter(d => d !== day.val) : [...currDays, day.val];
                                    setCurrent({ 
                                      ...current, 
                                      recurrence: { type: 'weekly', daysOfWeek: newDays } 
                                    });
                                }}
                            >
                                {day.label}
                            </Button>
                        )
                    })}
                </Stack>
            </Stack>
        )}

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
            select
            label="Tipo de descuento"
            value={current.discountType}
            onChange={(e) =>
                setCurrent({
                ...current,
                discountType: e.target.value as "percentage" | "fixed",
                })
            }
            fullWidth
            >
            <MenuItem value="percentage">Porcentaje (%)</MenuItem>
            <MenuItem value="fixed">Monto fijo ($)</MenuItem>
            </TextField>

            <TextField
            label="Valor del descuento"
            type="number"
            value={current.discountValue}
            onChange={(e) =>
                setCurrent({ ...current, discountValue: Number(e.target.value) })
            }
            fullWidth
            required
            />
        </Stack>

        <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
            <Box flex={1}>
                <Typography variant="subtitle2" fontWeight={700} mb={0.5}>Tamaños aplicables</Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                  (Si no seleccionas ninguno, aplica a todos)
                </Typography>
                <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto' }}>
                    <ProductSelector
                        products={products}
                        selected={current.productIds || []}
                        onChange={(ids) =>
                            setCurrent({ ...current, productIds: ids })
                        }
                    />
                </Paper>
            </Box>
            <Box flex={1}>
                <Typography variant="subtitle2" fontWeight={700} mb={0.5}>Divisiones aplicables</Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                  (Si no seleccionas ninguna, aplica a todas)
                </Typography>
                <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto' }}>
                    <SliceModeSelector
                        selected={current.sliceModes || []}
                        onChange={(modes) =>
                            setCurrent({ ...current, sliceModes: modes })
                        }
                    />
                </Paper>
            </Box>
            <Box flex={1}>
                <Typography variant="subtitle2" fontWeight={700} mb={0.5}>Sabores aplicables</Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                  (Si no seleccionas ninguno, aplica a todos)
                </Typography>
                <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto' }}>
                    <PizzaFlavorSelector
                        flavors={flavors}
                        selected={current.flavorIds || []}
                        onChange={(ids) =>
                            setCurrent({ ...current, flavorIds: ids })
                        }
                    />
                </Paper>
            </Box>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography fontWeight={700}>Activa manualmente</Typography>
          <Switch
            checked={current.active}
            color="success"
            onChange={() =>
              setCurrent({ ...current, active: !current.active })
            }
          />
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} pt={2} justifyContent="flex-end">
          <Button fullWidth={isMobile} onClick={() => setOpen(false)} color="inherit">
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="success"
            fullWidth={isMobile}
            disabled={!current.title || !current.startDate || !current.endDate}
            onClick={async () => {
              if (current.id) {
                await updateItem(current.id, current);
              } else {
                await createItem(current);
              }
              setOpen(false);
            }}
          >
            Guardar Promoción
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );

  return (
    <>
      {Header}

      {!open && !isMobile && (
        <Box sx={{ height: 520 }}>
          <PremiumDataGrid
            rows={promotions}
            columns={columns}
            getRowId={(row) => row.id!}
            onRowDoubleClick={(p) => {
              setCurrent(p.row as Promotion);
              setOpen(true);
            }}
          />
        </Box>
      )}

      {!open && isMobile && MobileList}

      {Form}
    </>
  );
}
